<template lang="pug">
.form.for-create
  .loader(v-if="loading || formBusy")
    q-spinner(color="primary", size="3em")

  q-form.form-data(v-if="!loading", @submit.prevent="submitForm")
    .header.form-card-title
      inline-svg.header-icon.form-card-title-icon(:src="require('../../../../assets/icons/form/header/add.svg')")
      .header-text {{ formTitle }}
      inline-svg.form-card-close-icon(
        :src="require('../../../../assets/icons/form/header/close.svg')",
        @click="requestClose"
      )

    .fields(v-show="!formBusy")
      form-fields(
        :fields="fields",
        :scheme="scheme",
        :values="formValues",
        :watchValues="formValues",
        :invalidFields="invalidFields",
        :schemaVersion="schemaVersion",
        :watchRequest="requestWatchedFieldValue",
        :watchParentsImmediate="true",
        @change="updateField",
        @initial-load="selectLoaded"
      )

    .buttons(v-if="buttons.length > 0 && !formBusy")
      .buttons-item(v-for="button in buttons", :key="button.name", :class="button.type", @click="buttonClick(button)")
        q-btn(
          flat,
          no-caps,
          :label="button.label",
          :disable="button.type === 'submit' && (submitting || dependencyLoading)"
        )

  q-dialog(v-model="workCategoryTemplateConfirmShown", @hide="resolveWorkCategoryTemplateConfirmation(false)")
    confirm-modal(
      @confirm="resolveWorkCategoryTemplateConfirmation",
      :parentData="{ message: workCategoryTemplateConfirmMessage }"
    )
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Notify } from "quasar";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { currentUserId } from "@/services/useAuth";
import { currentLocale, notifies } from "@/services/useLocales";
import { useRouter } from "@/routing/router";
import { useStore } from "@/store";
import i18n from "@/plugins/vue-i18n";
import ConfirmModal from "@/components/shared/helpers/confirmModal.vue";
import FormFields from "./FormFields.vue";
import {
  buildIssueFormRequestParams,
  buildIssueSubmissionValues,
  formFields as getFormFields,
  formScheme as getFormScheme,
  getLockedIssueContext,
  mergeLockedIssueContext,
  normalizeFormData,
  prepareIssueFormSchema,
  reconcileIssueFormValues,
  serializeIssueFieldValue,
  shouldRefetchIssueForm,
  valuesFromIssueFormSchema,
} from "@/services/useIssueCreateForm";

const props = defineProps({
  parentData: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["close-form"]);
const store = useStore();
const router = useRouter();

const loading = ref(true);
const schemaLoading = ref(false);
const dependencyLoading = ref(false);
const submitting = ref(false);
const formData = ref(null);
const formValues = ref({});
const invalidFields = ref({});
const selectsLoading = ref([]);
const schemaVersion = ref(0);
const lockedContext = ref(null);
const refetchingField = ref(null);
const workCategoryTemplateConfirmShown = ref(false);
let workCategoryTemplateConfirmResolver = null;
let schemaRequestId = 0;
let workCategoryDependencyRequestId = 0;

const formDataPath = computed(() => props.parentData.formDataPath || "");
const submitPath = computed(() => props.parentData.submitPath || store.state.paths.issue);
const formTitle = computed(() => formData.value?.title || "Создать заявку по обращению");
const fields = computed(() => getFormFields(formData.value));
const scheme = computed(() => getFormScheme(formData.value));
const buttons = computed(() => {
  const schemaButtons = formData.value?.components?.buttons;

  if (Array.isArray(schemaButtons) && schemaButtons.length > 0) return schemaButtons;

  return [{ name: "submit", type: "submit", label: "Сохранить" }];
});
const formBusy = computed(() => schemaLoading.value || selectsLoading.value.length > 0);
const workCategoryTemplateConfirmMessage = computed(() => i18n.messages[currentLocale.value].applyServiceTemplates);

const responseFormData = response => {
  if (response?.components || response?.fields) return response;
  if (response?.data?.components || response?.data?.fields) return response.data;
  if (response?.data?.data?.components || response?.data?.data?.fields) return response.data.data;

  return response?.data || response;
};

const responseData = response => response?.data?.data || response?.data || response;

const fetchFormData = params => backend.index(formDataPath.value, { params });

const fetchPprEquipmentLink = async workCategoryId => {
  const path = `${store.state.paths.work_category}/ppr_equipment_link`;
  const params = { work_category_id: workCategoryId };

  return responseData(await backend.index(path, { params }));
};

const fetchCustomCkeditor = async workCategoryId => {
  const path = `${store.state.paths.issue}/custom_ckeditor`;
  const params = { work_category_id: workCategoryId };

  return responseData(await backend.index(path, { params }));
};

const requestWatchedFieldValue = async ({ params, parentParams }) => {
  const configuredPath = parentParams.path || "";
  const basePath = configuredPath.startsWith("/")
    ? configuredPath
    : store.state.paths[configuredPath] || configuredPath;
  const path = `${basePath}${parentParams.action || ""}`;

  return responseData(await backend.index(path, { params }));
};

const fieldHasValue = value =>
  Array.isArray(value)
    ? value.some(item => item !== undefined && item !== null && item !== "")
    : value !== undefined && value !== null && value !== "";

const fieldHasOptionsPath = field => {
  if (field.full_options_path) return true;
  if (typeof field.options_path === "string" && field.options_path.startsWith("/")) return true;

  return Boolean(field.options_path && store.state.paths[field.options_path]);
};

const selectFieldsWaitingForOptions = schema =>
  getFormFields(schema)
    .filter(field => {
      if (field.type !== "select" || !fieldHasOptionsPath(field)) return false;

      const disabled = Boolean(field.disabled || field.readonly);

      return !disabled || fieldHasValue(field.value);
    })
    .map(field => field.name);

const applyFormData = ({ data, changedFieldName = null, changedFieldValue }) => {
  let nextFormData = normalizeFormData(data);

  if (!lockedContext.value) {
    lockedContext.value = getLockedIssueContext(nextFormData);
  } else {
    nextFormData = mergeLockedIssueContext(nextFormData, lockedContext.value);
  }

  const nextValues = changedFieldName
    ? reconcileIssueFormValues({
        previousValues: formValues.value,
        formData: nextFormData,
        changedFieldName,
        changedFieldValue,
        lockedValues: lockedContext.value?.values || {},
      })
    : valuesFromIssueFormSchema(nextFormData);

  nextFormData = prepareIssueFormSchema(nextFormData, nextValues);
  formData.value = nextFormData;
  formValues.value = nextValues;
  selectsLoading.value = selectFieldsWaitingForOptions(nextFormData);
  schemaVersion.value += 1;
};

const removeFormField = name => {
  const normalizedFormData = normalizeFormData(formData.value);
  const nextFields = getFormFields(normalizedFormData).filter(field => field.name !== name);

  if (nextFields.length === getFormFields(normalizedFormData).length) return;

  const nextValues = { ...formValues.value };
  const nextInvalidFields = { ...invalidFields.value };

  delete nextValues[name];
  delete nextInvalidFields[name];

  formData.value = {
    ...normalizedFormData,
    components: {
      ...normalizedFormData.components,
      fields: nextFields,
      scheme: getFormScheme(normalizedFormData)
        .map(row => row.filter(item => item.name !== name))
        .filter(row => row.length > 0),
    },
  };
  formValues.value = nextValues;
  invalidFields.value = nextInvalidFields;
};

const setFormFieldValue = (name, value) => {
  const normalizedFormData = normalizeFormData(formData.value);

  if (!getFormFields(normalizedFormData).some(field => field.name === name)) return;

  formData.value = {
    ...normalizedFormData,
    components: {
      ...normalizedFormData.components,
      fields: getFormFields(normalizedFormData).map(field => (field.name === name ? { ...field, value } : field)),
    },
  };
  formValues.value = { ...formValues.value, [name]: value };
};

const setFormFieldVisibility = (name, visible) => {
  const normalizedFormData = normalizeFormData(formData.value);

  if (!getFormFields(normalizedFormData).some(field => field.name === name)) return;

  formData.value = {
    ...normalizedFormData,
    components: {
      ...normalizedFormData.components,
      fields: getFormFields(normalizedFormData).map(field => (field.name === name ? { ...field, visible } : field)),
    },
  };
};

const hasTemplateValue = value => {
  if (typeof value === "string") return value.trim().length > 0;

  return value !== undefined && value !== null && value !== "";
};

const requestWorkCategoryTemplateConfirmation = () =>
  new Promise(resolve => {
    workCategoryTemplateConfirmResolver = resolve;
    workCategoryTemplateConfirmShown.value = true;
  });

const resolveWorkCategoryTemplateConfirmation = value => {
  const resolve = workCategoryTemplateConfirmResolver;

  workCategoryTemplateConfirmResolver = null;
  workCategoryTemplateConfirmShown.value = false;
  resolve?.(Boolean(value));
};

const applyPprEquipmentLink = data => {
  if (data?.value) return;

  const equipmentIsLocked = Boolean(lockedContext.value?.fields?.some(field => field.name === "ppr_equipment_id"));

  if (!equipmentIsLocked) removeFormField("ppr_equipment_id");
};

const applyCustomCkeditor = async (data, requestId) => {
  const workCategoryDescription = data?.work_category_description;

  if (hasTemplateValue(workCategoryDescription)) {
    setFormFieldValue("work_category_description", workCategoryDescription);
    setFormFieldVisibility("work_category_description", true);
  } else {
    setFormFieldValue("work_category_description", "");
    setFormFieldVisibility("work_category_description", false);
  }

  const descriptionTemplate = data?.description;

  if (!hasTemplateValue(descriptionTemplate)) return;

  const previousDescription = formValues.value.description;
  let shouldApply = !hasTemplateValue(previousDescription) || previousDescription === descriptionTemplate;

  if (!shouldApply) shouldApply = await requestWorkCategoryTemplateConfirmation();
  if (requestId !== workCategoryDependencyRequestId) return;

  if (shouldApply) setFormFieldValue("description", descriptionTemplate);
};

const loadWorkCategoryDependencies = async workCategoryId => {
  const requestId = ++workCategoryDependencyRequestId;

  dependencyLoading.value = true;

  try {
    try {
      const pprEquipmentLink = await fetchPprEquipmentLink(workCategoryId);

      if (requestId !== workCategoryDependencyRequestId) return;
      applyPprEquipmentLink(pprEquipmentLink);
    } catch (error) {
      if (requestId === workCategoryDependencyRequestId) await handleError(error);
    }

    try {
      const customCkeditor = await fetchCustomCkeditor(workCategoryId);

      if (requestId !== workCategoryDependencyRequestId) return;
      await applyCustomCkeditor(customCkeditor, requestId);
    } catch (error) {
      if (requestId === workCategoryDependencyRequestId) await handleError(error);
    }
  } finally {
    if (requestId === workCategoryDependencyRequestId) dependencyLoading.value = false;
  }
};

const loadInitialForm = async () => {
  const params = { ...(props.parentData.params || {}) };

  if (currentUserId.value) params.request_user_id = currentUserId.value;

  try {
    const response = await fetchFormData(params);
    applyFormData({ data: responseFormData(response) });
  } catch (error) {
    await handleError(error);
    requestClose();
  } finally {
    loading.value = false;
  }
};

const refetchForm = async ({ name, value }) => {
  const requestId = ++schemaRequestId;
  const requestValues = { ...formValues.value, [name]: value };
  const params = {
    ...(props.parentData.params || {}),
    ...buildIssueFormRequestParams(formData.value, requestValues),
  };

  if (name === "service_id") delete params.work_category_id;
  if (currentUserId.value) params.request_user_id = currentUserId.value;

  refetchingField.value = name;
  schemaLoading.value = true;

  try {
    const response = await fetchFormData(params);

    if (requestId !== schemaRequestId) return false;

    applyFormData({ data: responseFormData(response), changedFieldName: name, changedFieldValue: value });
    return true;
  } catch (error) {
    if (requestId === schemaRequestId) await handleError(error);
    return false;
  } finally {
    if (requestId === schemaRequestId) {
      refetchingField.value = null;
      schemaLoading.value = false;
    }
  }
};

const updateField = async ({ name, field, value }) => {
  delete invalidFields.value[name];

  const serializedValue = serializeIssueFieldValue(value);
  formValues.value = { ...formValues.value, [name]: serializedValue };

  // work_category_id resets itself when service_id changes. The service refetch already
  // covers this state change, so a second schema request would reconcile against the wrong parent.
  if (refetchingField.value === "service_id" && name === "work_category_id") return;

  if (name === "work_category_id") {
    if (valueIsEmpty(serializedValue)) {
      workCategoryDependencyRequestId += 1;
      dependencyLoading.value = false;
      resolveWorkCategoryTemplateConfirmation(false);
      setFormFieldValue("work_category_description", "");
      setFormFieldVisibility("work_category_description", false);
    } else {
      await loadWorkCategoryDependencies(serializedValue);
    }

    return;
  }

  if (shouldRefetchIssueForm(name, field)) {
    if (name === "service_id") {
      workCategoryDependencyRequestId += 1;
      dependencyLoading.value = false;
      resolveWorkCategoryTemplateConfirmation(false);
    }

    await refetchForm({ name, value: serializedValue });
  }
};

const selectLoaded = name => {
  selectsLoading.value = selectsLoading.value.filter(fieldName => fieldName !== name);
};

const valueIsEmpty = value =>
  value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

const validateForm = () => {
  invalidFields.value = fields.value.reduce((result, field) => {
    if (Boolean(field.require ?? field.required) && valueIsEmpty(formValues.value[field.name])) {
      result[field.name] = "Поле обязательно для заполнения";
    }

    return result;
  }, {});

  return Object.keys(invalidFields.value).length === 0;
};

const showCreatedNotification = response => {
  const issue = responseData(response);
  const issueId = issue?.id;
  const facilityId = issue?.facility_id;
  const entry = issueId ? `${notifies.value.entry_number}${issueId}` : notifies.value.entry_without_number;
  const notifyOptions = {
    message: `${entry}${notifies.value.is_success}${notifies.value.created}`,
  };

  if (issueId && facilityId) {
    notifyOptions.timeout = 20000;
    notifyOptions.actions = [
      {
        label: i18n.messages[currentLocale.value].openIssue,
        style: "color: #5b7aff",
        handler: () => {
          router.push({
            name: "DynamicIssues",
            params: { facility_id: facilityId },
            query: { id: issueId, show: "true" },
          });
        },
      },
    ];
  }

  Notify.create(notifyOptions);
};

const submitForm = async () => {
  if (submitting.value || dependencyLoading.value || formBusy.value || !validateForm()) {
    if (Object.keys(invalidFields.value).length > 0) Notify.create("Исправьте ошибки в полях формы");

    return;
  }

  submitting.value = true;

  const root = formData.value?.root || "issue";
  const params = {
    [root]: {
      ...buildIssueSubmissionValues(formValues.value, formData.value),
      ...(formData.value?.additional_data || {}),
      ...(props.parentData.additionalData || {}),
    },
  };

  if (currentUserId.value) params.request_user_id = currentUserId.value;

  try {
    const response = await backend.create(submitPath.value, "", params);
    showCreatedNotification(response);
    emit("close-form", { data: response.data, method: "create" });
  } catch (error) {
    const details = error?.response?.data?.details || error?.response?.data?.errors;

    if (error?.response?.status === 422 && details && typeof details === "object") {
      invalidFields.value = Object.entries(details).reduce((result, [name, messages]) => {
        if (name !== "base") result[name] = Array.isArray(messages) ? messages[0]?.message || messages[0] : messages;

        return result;
      }, {});

      const baseErrors = details.base;
      if (baseErrors) {
        const message = [baseErrors]
          .flat()
          .map(errorItem => errorItem?.message || errorItem)
          .join(". ");
        Notify.create(message);
      } else {
        Notify.create("Исправьте ошибки в полях формы");
      }
    } else {
      await handleError(error);
    }
  } finally {
    submitting.value = false;
  }
};

const buttonClick = button => {
  if (button.type === "cancel") requestClose();
  if (button.type === "submit") submitForm();
};

const requestClose = () => {
  emit("close-form");
};

onMounted(loadInitialForm);

defineExpose({ requestClose });
</script>

<style scoped lang="scss">
.form {
  .loader {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
  }

  .form-data {
    .header {
      color: var(--modal-form-card-color);
      display: flex;
      font-size: 18px;
      font-weight: 600;
      gap: 10px;

      .header-icon {
        margin: 0;
      }

      .form-card-close-icon {
        top: 8px;
        right: 8px;
      }
    }

    .fields {
      margin-top: 32px;
    }

    .buttons {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin-top: 34px;
      position: sticky;
      bottom: 0;
      padding: 40px 0;

      &-item {
        transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), opacity 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);
        border-radius: 8px;
        box-shadow: var(--modal-form-cancel-box-shadow);
        min-width: 95px;
        min-height: 36px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        line-height: 36px;
        padding: 0 70px;
        cursor: pointer;
        white-space: nowrap;

        &.submit {
          background-color: #5b7aff;
          color: #fff;
        }

        &.cancel {
          color: var(--modal-form-cancel-color);
          background-color: var(--modal-form-cancel-background);

          &:hover {
            background-color: rgba(91, 122, 255, 0.15);
          }
        }
      }
    }
  }
}
</style>
